import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Card } from '../ui/Card';
import { GA4AccountSelector } from './GA4AccountSelector';
import { X, Building, Globe, Tag, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
}

const INDUSTRIES = [
  { value: 'technology', label: 'Technology' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance' },
  { value: 'education', label: 'Education' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'retail', label: 'Retail' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'other', label: 'Other' },
];

const STEPS = [
  { id: 'project-details', title: 'Project Details', description: 'Basic information about your project' },
  { id: 'ga4-connection', title: 'Connect GA4', description: 'Link your Google Analytics property' },
  { id: 'review', title: 'Review & Create', description: 'Confirm your project setup' }
];

export function CreateProjectModal({ isOpen, onClose, onSubmit, isLoading = false }: CreateProjectModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    websiteUrl: '',
    industry: ''
  });
  const [ga4Selection, setGA4Selection] = useState<{
    accountId: string;
    accountName: string;
    propertyId: string;
    propertyName: string;
  } | null>(null);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const projectData = {
      ...formData,
      ga4Connection: ga4Selection
    };
    
    console.log('Creating project with data:', {
      ...projectData,
      ga4Connection: ga4Selection ? {
        accountId: ga4Selection.accountId,
        accountName: ga4Selection.accountName,
        propertyId: ga4Selection.propertyId,
        propertyName: ga4Selection.propertyName
      } : null
    });
    
    onSubmit(projectData);
  };

  const canProceedFromStep = (step: number) => {
    switch (step) {
      case 0: // Project details
        return formData.name.trim() !== '';
      case 1: // GA4 connection
        return true; // GA4 connection is optional
      case 2: // Review
        return true;
      default:
        return false;
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setFormData({
      name: '',
      description: '',
      websiteUrl: '',
      industry: ''
    });
    setGA4Selection(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create New Project</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  index < currentStep 
                    ? 'bg-green-500 text-white' 
                    : index === currentStep 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>
                  {index < currentStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {currentStep === 0 && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {STEPS[0].title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {STEPS[0].description}
                  </p>
                </div>

                {/* Project Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Project Name *
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter project name"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Brief description of your project"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>

                {/* Website URL */}
                <div>
                  <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Website URL
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                    <Input
                      id="websiteUrl"
                      type="url"
                      value={formData.websiteUrl}
                      onChange={(e) => handleChange('websiteUrl', e.target.value)}
                      placeholder="https://example.com"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Industry */}
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Industry
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4 z-10" />
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => handleChange('industry', value)}
                      placeholder="Select industry"
                      options={INDUSTRIES}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {STEPS[1].title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {STEPS[1].description}
                  </p>
                </div>

                <GA4AccountSelector
                  onSelectionChange={setGA4Selection}
                  showSkipOption={true}
                  onSkip={handleNext}
                />
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {STEPS[2].title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {STEPS[2].description}
                  </p>
                </div>

                {/* Project Summary */}
                <Card className="p-4 bg-gray-50 dark:bg-gray-800">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Project Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <Building className="w-4 h-4 text-gray-500 mr-2" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Name:</strong> {formData.name}
                      </span>
                    </div>
                    {formData.description && (
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-0.5">📝</span>
                        <span className="text-gray-700 dark:text-gray-300">
                          <strong>Description:</strong> {formData.description}
                        </span>
                      </div>
                    )}
                    {formData.websiteUrl && (
                      <div className="flex items-center">
                        <Globe className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-gray-700 dark:text-gray-300">
                          <strong>Website:</strong> {formData.websiteUrl}
                        </span>
                      </div>
                    )}
                    {formData.industry && (
                      <div className="flex items-center">
                        <Tag className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-gray-700 dark:text-gray-300">
                          <strong>Industry:</strong> {INDUSTRIES.find(i => i.value === formData.industry)?.label}
                        </span>
                      </div>
                    )}
                  </div>
                </Card>

                {/* GA4 Connection Summary */}
                <Card className="p-4 bg-gray-50 dark:bg-gray-800">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">GA4 Connection</h4>
                  {ga4Selection ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-gray-700 dark:text-gray-300">
                          <strong>Account:</strong> {ga4Selection.accountName}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-gray-700 dark:text-gray-300">
                          <strong>Property:</strong> {ga4Selection.propertyName}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <X className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-500 dark:text-gray-400">
                        GA4 connection will be skipped (can be added later)
                      </span>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={currentStep === 0 ? handleClose : handleBack}
              disabled={isLoading}
            >
              {currentStep === 0 ? (
                'Cancel'
              ) : (
                <>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </>
              )}
            </Button>

            <Button
              onClick={currentStep === STEPS.length - 1 ? handleSubmit : handleNext}
              disabled={isLoading || !canProceedFromStep(currentStep)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </div>
              ) : currentStep === STEPS.length - 1 ? (
                'Create Project'
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}